import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Download, X, CheckCircle, CreditCard, Building2, Phone, MapPin, Shield } from "lucide-react";
import DOTCompanyFetchModal from "./dot-company-fetch-modal";
import { insertCompanySchema } from "@shared/schema";

const formSchema = insertCompanySchema.extend({
  name: z.string().min(1, "Company name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  dotNumber: z.string().regex(/^\d*$/, "DOT number must be numeric only").optional().or(z.literal("")),
  dbaName: z.string().optional().or(z.literal("")),
  contactPersonName: z.string().optional(),
  contactPersonEmail: z.string().optional(),
  contactPersonPhone: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface OfficerToCreate {
  firstName: string;
  lastName: string;
  email: string | null;
}

export interface CreateCompanyResult {
  company: FormData;
  officersToCreate?: OfficerToCreate[];
  hasDotData: boolean;
  isServiceBureau: boolean;
}

interface CreateCompanyModalProps {
  onClose: () => void;
  onSubmit: (data: FormData, officersToCreate?: OfficerToCreate[], hasDotData?: boolean) => void;
  isLoading: boolean;
}

export default function CreateCompanyModal({
  onClose,
  onSubmit,
  isLoading
}: CreateCompanyModalProps) {
  const [showDOTFetchModal, setShowDOTFetchModal] = useState(false);
  const [officersToCreate, setOfficersToCreate] = useState<OfficerToCreate[]>([]);
  const [dotDataFetched, setDotDataFetched] = useState(false);
  const [fetchedDotNumber, setFetchedDotNumber] = useState<string | null>(null);
  const [dotProfileFields, setDotProfileFields] = useState<Record<string, any>>({});
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      dbaName: "",
      type: "direct_client",
      ein: "",
      status: "inquiry",
      dotNumber: "",
      inactiveDotNumbers: "",
      email: "",
      phone: "",
      address: "",
      contactPersonName: "",
      contactPersonEmail: "",
      contactPersonPhone: "",
      dotPin: "",
      loginGovUsername: "",
      loginGovAuthMethod: undefined,
      billingPreference: "pay_at_service",
      batchInterval: null,
    },
  });

  const selectedType = form.watch("type");
  const dotNumber = form.watch("dotNumber");
  const billingPreference = form.watch("billingPreference");

  if (dotDataFetched && fetchedDotNumber && dotNumber !== fetchedDotNumber) {
    setDotDataFetched(false);
    setFetchedDotNumber(null);
    setDotProfileFields({});
  }

  const handleFormSubmit = (data: FormData) => {
    const hasDotData = dotDataFetched && !!data.dotNumber;
    const mergedData = hasDotData 
      ? { ...data, ...dotProfileFields, name: data.name, dbaName: data.dbaName, phone: data.phone, email: data.email, address: data.address, dotNumber: data.dotNumber, rawResponse: fetchRawResponse }
      : data;
    onSubmit(mergedData, officersToCreate.length > 0 ? officersToCreate : undefined, hasDotData);
  };

  const [fetchRawResponse, setFetchRawResponse] = useState<any>(null);

  const handleDOTDataApply = (data: {
    companyFields: Record<string, any>;
    officersToCreate: OfficerToCreate[];
    rawResponse?: any;
  }) => {
    if (data.companyFields.name) form.setValue("name", data.companyFields.name);
    if (data.companyFields.dbaName) form.setValue("dbaName", data.companyFields.dbaName);
    if (data.companyFields.phone) form.setValue("phone", data.companyFields.phone);
    if (data.companyFields.email) form.setValue("email", data.companyFields.email);
    if (data.companyFields.address) form.setValue("address", data.companyFields.address);
    if (data.companyFields.dotNumber) {
      form.setValue("dotNumber", data.companyFields.dotNumber);
      setFetchedDotNumber(data.companyFields.dotNumber);
    }
    setDotProfileFields(data.companyFields);
    setOfficersToCreate(data.officersToCreate);
    if (data.rawResponse) setFetchRawResponse(data.rawResponse);
    setDotDataFetched(true);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add New Company</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-5 overflow-y-auto flex-1 px-1">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-company-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="service_bureau">Service Bureau</SelectItem>
                        <SelectItem value="direct_client">Direct Client</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-company-status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="inquiry">Inquiry</SelectItem>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {selectedType !== "service_bureau" && (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <FormLabel className="text-base font-medium">DOT Number</FormLabel>
                {dotDataFetched && (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3 h-3" />
                    Data fetched
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <FormField
                  control={form.control}
                  name="dotNumber"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input placeholder="Enter DOT number to auto-fill company data" {...field} data-testid="input-dot-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setShowDOTFetchModal(true)}
                  disabled={!dotNumber || dotNumber.trim() === ""}
                  data-testid="button-open-dot-fetch"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Fetch DOT Data
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Enter a DOT number and click "Fetch DOT Data" to automatically populate company information
              </p>
            </div>
            )}

            {selectedType !== "service_bureau" && officersToCreate.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-blue-800">Officers to create as contacts:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  {officersToCreate.map((officer, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span>{officer.firstName} {officer.lastName}</span>
                      {officer.email && (
                        <span className="text-blue-500 text-xs">({officer.email})</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setOfficersToCreate(prev => prev.filter((_, i) => i !== index))}
                        className="text-blue-400 hover:text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Building2 className="h-4 w-4" />
              <span>Company Information</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} data-testid="input-company-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dbaName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DBA Name (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Doing Business As" {...field} data-testid="input-dba-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="ein"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>EIN (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter company EIN" {...field} value={field.value ?? ""} data-testid="input-ein" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedType !== "service_bureau" && (
              <FormField
                control={form.control}
                name="inactiveDotNumbers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Inactive DOT Numbers (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter old or inactive DOT numbers" {...field} value={field.value ?? ""} data-testid="input-inactive-dot-numbers" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <Separator />

            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Phone className="h-4 w-4" />
              <span>Contact Information</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="company@example.com" {...field} data-testid="input-email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="(555) 123-4567" {...field} value={field.value ?? ""} data-testid="input-phone" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter company address" {...field} value={field.value ?? ""} rows={2} data-testid="input-address" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedType !== "service_bureau" && (
              <>
                <Separator />

                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Shield className="h-4 w-4" />
                  <span>Government Credentials</span>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="dotPin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>DOT PIN (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter DOT PIN" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="loginGovUsername"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Login.gov Username (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter Login.gov username" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="loginGovAuthMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Login.gov Auth Method</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select method" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="face_touch_unlock">Face or touch unlock</SelectItem>
                            <SelectItem value="authentication_application">Authentication app</SelectItem>
                            <SelectItem value="security_key">Security key</SelectItem>
                            <SelectItem value="text_call">Text/call</SelectItem>
                            <SelectItem value="backup_code">Backup code</SelectItem>
                            <SelectItem value="piv_cac">PIV or CAC</SelectItem>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            <Separator />
            
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <CreditCard className="h-4 w-4" />
              <span>Billing Settings</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="billingPreference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Preference</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        if (value === "pay_at_service") {
                          form.setValue("batchInterval", null);
                        }
                      }}
                      value={field.value || "pay_at_service"}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-billing-preference">
                          <SelectValue placeholder="Select billing preference" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pay_at_service">Pay at Service</SelectItem>
                        <SelectItem value="batch">Batch Billing</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {field.value === "batch" 
                        ? "Invoices will be grouped and sent on a schedule" 
                        : "Invoice sent immediately after each service"}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {billingPreference === "batch" && (
                <FormField
                  control={form.control}
                  name="batchInterval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Batch Interval</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-batch-interval">
                            <SelectValue placeholder="Select interval" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Biweekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How often to send batch invoices
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t mt-4 sticky bottom-0 bg-background pb-1">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Company"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      {showDOTFetchModal && (
        <DOTCompanyFetchModal
          open={showDOTFetchModal}
          onClose={() => setShowDOTFetchModal(false)}
          dotNumber={dotNumber || ""}
          mode="create"
          onApply={handleDOTDataApply}
        />
      )}
    </Dialog>
  );
}
